import { useId, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { useAuth } from "@/auth/AuthProvider";
import { ApiError } from "@/lib/api";
import {
  documentRejection,
  uploadSubmissionDocument,
  type BondDocument,
} from "@/lib/brokerBonds";
import {
  acceptAttribute,
  documentRuleMessage,
  screenDocument,
  type DocumentRules,
} from "@/lib/brokerDocumentTypes";
import { messageFor } from "@/lib/errors";
import { FileDropTarget } from "@/components/FileDropTarget";
import { SelectField } from "@/components/SelectField";
import { buttonClass } from "@/components/buttonStyles";

// BRK-S424 — "Upload document" on a FILED submission: a type select over what
// the caller offers (the served types, the post-submit named types and the
// checklist's own, `documentUploadTypes`) and a drop target, posting the
// post-submit route (PMS-S843). The AccountDocumentSlot pattern
// on the ORDER's route.
//
// NOTHING HERE IS OPTIMISTIC (NN #7): the caller hears `onUploaded` only from
// the POST's 2xx and re-reads the list; a refusal is said inline, in the same
// words the client-side screen uses, and the dialog stays open.

export function SubmissionDocumentUploadDialog({
  orderId,
  open,
  initialType,
  types,
  rules,
  rulesError,
  onClose,
  onUploaded,
}: {
  orderId: string;
  open: boolean;
  /** A required row's type, preselected; "" to let the broker choose. */
  initialType: string;
  types: readonly { value: string; label: string }[];
  /** PMS's upload rules; null while read or when the read failed. */
  rules: DocumentRules | null;
  rulesError: string | null;
  onClose: () => void;
  onUploaded: (document: BondDocument) => void;
}) {
  const titleId = useId();
  const theme = useTheme();
  // Full screen on a phone; a dialog from `sm:`.
  const phone = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const [busy, setBusy] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      aria-labelledby={titleId}
      fullWidth
      maxWidth="sm"
      fullScreen={phone}
    >
      <DialogTitle id={titleId}>Upload a document</DialogTitle>
      {/* Mounted per opening, so a closed dialog keeps no draft (NN #9). */}
      {open && (
        <UploadForm
          orderId={orderId}
          initialType={initialType}
          types={types}
          rules={rules}
          rulesError={rulesError}
          busy={busy}
          setBusy={setBusy}
          onClose={onClose}
          onUploaded={onUploaded}
        />
      )}
    </Dialog>
  );
}

function UploadForm({
  orderId,
  initialType,
  types,
  rules,
  rulesError,
  busy,
  setBusy,
  onClose,
  onUploaded,
}: {
  orderId: string;
  initialType: string;
  types: readonly { value: string; label: string }[];
  rules: DocumentRules | null;
  rulesError: string | null;
  busy: boolean;
  setBusy: (busy: boolean) => void;
  onClose: () => void;
  onUploaded: (document: BondDocument) => void;
}) {
  const { authedUpload } = useAuth();
  const [type, setType] = useState(initialType);
  const [error, setError] = useState<string | null>(null);

  async function upload(file: File) {
    setError(null);
    if (type === "") {
      setError("Choose what kind of document this is first.");
      return;
    }
    if (!rules) return;
    const rejection = screenDocument(file, rules);
    if (rejection) {
      setError(rejection.message);
      return;
    }
    setBusy(true);
    try {
      onUploaded(
        await uploadSubmissionDocument(authedUpload, orderId, { file, type }),
      );
    } catch (err) {
      const refused = documentRejection(err);
      setError(
        refused
          ? documentRuleMessage(refused.rule, refused.filename, rules)
          : err instanceof ApiError
            ? messageFor(err)
            : "We couldn’t upload that file. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <DialogContent>
        <div className="flex min-w-0 flex-col gap-4">
          <SelectField
            label="Document type"
            placeholder="Select…"
            required
            options={[...types]}
            value={type}
            disabled={busy}
            onChange={(e) => {
              setType(e.target.value);
              setError(null);
            }}
          />
          {rulesError && (
            <p role="alert" className="text-sm text-brk-danger">
              {rulesError}
            </p>
          )}
          {rules ? (
            <FileDropTarget
              prompt="Drag & drop a document, or"
              hint="One file at a time."
              inputLabel="Upload a document to this submission"
              accept={acceptAttribute(rules)}
              multiple={false}
              disabled={busy}
              onFiles={(files) => {
                const [file] = files;
                if (file) void upload(file);
              }}
            />
          ) : (
            !rulesError && (
              <p role="status" className="text-sm text-brk-dim">
                Loading…
              </p>
            )
          )}
          {busy && (
            <p role="status" className="text-sm text-brk-dim">
              Uploading…
            </p>
          )}
          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className={buttonClass({ variant: "secondary" })}
        >
          Cancel
        </button>
      </DialogActions>
    </>
  );
}
